import axios from "axios";
import api from "../../../";
import type { ApiResponse } from "../../../types";
import type { PaginatedResponse } from "../../community/types";
import type {
  AuthorizationCredential,
  GetAuthorizationCredentialByIdParams,
  GetAuthorizationCredentialsParams,
} from "./types";

type AuthorizationCredentialsListApiResponse = {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: AuthorizationCredential[];
};

function serializeAuthorizationCredentialParams(
  params: Record<string, unknown>,
): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        searchParams.append(key, String(item));
      });
      return;
    }

    searchParams.append(key, String(value));
  });

  return searchParams.toString();
}

function normalizeAuthorizationCredentialsListResponse(
  payload: unknown,
): PaginatedResponse<AuthorizationCredential> {
  const body =
    payload &&
    typeof payload === "object" &&
    "results" in payload &&
    Array.isArray((payload as AuthorizationCredentialsListApiResponse).results)
      ? (payload as AuthorizationCredentialsListApiResponse)
      : payload &&
          typeof payload === "object" &&
          "data" in payload &&
          (payload as { data?: AuthorizationCredentialsListApiResponse }).data
        ? (payload as { data: AuthorizationCredentialsListApiResponse }).data
        : null;

  const results = body?.results || [];

  return {
    count: body?.count ?? results.length,
    next: body?.next ?? null,
    previous: body?.previous ?? null,
    results,
  };
}

export const getAuthorizationCredentials = async ({
  params,
  signal,
}: GetAuthorizationCredentialsParams = {}): Promise<
  PaginatedResponse<AuthorizationCredential>
> => {
  return await api
    .get<AuthorizationCredentialsListApiResponse>(
      "/admin/sepehr/authorization-credentials/",
      {
        ...(params ? { params } : {}),
        signal,
        paramsSerializer: {
          serialize: serializeAuthorizationCredentialParams,
        },
      },
    )
    .then((res) => normalizeAuthorizationCredentialsListResponse(res.data))
    .catch((error) => {
      if (axios.isCancel(error)) {
        throw error;
      }

      return {
        count: 0,
        next: null,
        previous: null,
        results: [],
      };
    });
};

export const getAuthorizationCredentialById = async ({
  id,
}: GetAuthorizationCredentialByIdParams): Promise<AuthorizationCredential | null> => {
  return await api
    .get<ApiResponse<AuthorizationCredential>>(
      `/admin/sepehr/authorization-credentials/${id}/`,
    )
    .then((res) => res.data.data)
    .catch(() => null);
};
